import * as Sentry from '@sentry/nextjs';
import { Call, WalletClient } from 'viem';

import { ClientInputError } from '@/lib/errors/client';

import { USDT_ADDRESSES } from '@/constants/addresses';
import { SUPPORTED_CHAINS_BY_CHAIN_ID } from '@/constants/chain';

import { getApproveABI } from '@/hooks/useApprove';

import { Transaction } from '@/utils/0x.types';
import { EthereumAddress } from '@/utils/models';
import { sleep } from '@/utils/sleep';
import { TokenInfo } from '@/utils/token.types';

interface FillBatchOrderArgs {
  allowance: bigint;
  chainId: number;
  connectorId: string | undefined;
  sellAmount: bigint;
  sellToken: TokenInfo;
  spender: EthereumAddress;
  transaction: Transaction;
  walletClient: WalletClient;
}

/**
 * Fill a batch order
 *
 * @remarks
 * Use this function to fill a batch approval & swap transaction.
 *
 * @param args - The arguments for the fill batch order transaction
 * @returns string - The call id of the batch transaction
 */
export const fillBatchOrder = async ({
  allowance,
  chainId,
  connectorId,
  sellAmount,
  sellToken,
  spender,
  transaction,
  walletClient,
}: FillBatchOrderArgs): Promise<string> => {
  return Sentry.startSpan(
    {
      name: 'fillBatchOrder',
      op: 'submitTransaction',
    },
    async (span) => {
      // set attributes on the span
      span.setAttribute('user.account.address', walletClient?.account?.address ?? 'no_address');
      span.setAttribute('user.account.chainID', chainId);

      // validate
      if (!transaction) throw new ClientInputError('No transaction data provided!');
      if (!walletClient || !walletClient.account)
        throw new ClientInputError('No account provided!');
      if (!chainId) throw new ClientInputError('No chainId provided!');

      const { to, data, value } = transaction;

      const calls: Call[] = [
        // approval transaction: batched approve+swap always uses an exact-amount
        // allowance since there's no follow-up swap to benefit from a cached max.
        {
          to: sellToken.address as EthereumAddress,
          functionName: 'approve',
          abi: getApproveABI(sellToken.address, sellToken.chainId),
          args: [spender, sellAmount],
        },
        // swap transaction
        {
          to,
          data,
          value: !!value ? BigInt(value) : undefined,
        },
      ];

      // If the token is USDT, we need to approve the spender for 0 amount first
      if (allowance > 0n && sellToken.address === USDT_ADDRESSES[chainId]) {
        calls.unshift({
          to: sellToken.address as EthereumAddress,
          functionName: 'approve',
          abi: getApproveABI(sellToken.address, sellToken.chainId),
          args: [spender, 0n],
        });
      }

      // Note: Rabby.io currently has a race condition when signing EIP-712 objects immediately before transactions.
      // To mitigate this, they recommend setting a timeout before sending the transaction.
      // This workaround can be removed once the issue is resolved:
      // See: https://github.com/RabbyHub/Rabby/issues/2146
      if (connectorId === 'io.rabby' || connectorId === 'com.brave.wallet') {
        await sleep(1000);
      }

      const { id } = await walletClient.sendCalls({
        account: walletClient.account,
        chain: SUPPORTED_CHAINS_BY_CHAIN_ID[chainId],
        calls,
      });

      return id;
    },
  );
};
